import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Programmers are optimists",
  "description": "",
  "published": "2012-05-25T00:00:00.000Z",
  "redirect_from": ["/blog/programmers-are-optimists/swizec/4509"],
  "categories": "Algorithm, Document Object Model, HTML, Opinions, Optimism, Personal, Programmer, Programming, There are known knowns",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Programmers are the most optimistic bunch of people I have ever come across. Ask us how long something will take and chances are our estimates will be way off.`}</p>
    <p><img parentName="p" {...{
        "src": "/d0a31797dcedc4a9d7b1b2c8f55ce62f/English-Programmerh26j25.gif",
        "alt": "English: Programmer",
        "title": "English: Programmer"
      }}></img></p>
    <p>{`It's not because we hate you. It's not because we didn't know any better, either. We're just very optimistic about everything. The project, our skills, the tech, your specs, the unknown unknowns ...`}</p>
    <p>{`Especially the unknown unknowns are a big problem - it's impossible to predict everything that might go wrong. The variables are just too many.`}</p>
    <p>{`Right now it looks like we can do This (tm) in an hour, but a weirdness or twenty and suddenly it's taking a whole day.`}</p>
    <p>{`As programmers we assume the least complications, even when multiplying by Pi.`}</p>
    <p>{`But there's another profession that gives a lot of predictions in the face of uncertainty - doctors. When a doctor gets their estimates wrong, people are often delighted. What?`}</p>
    <p>{`Let me tell you a tale of two estimates - a conservative and a programmer estimate.`}</p>
    <h2 {...{
      "id": "a-tale-of-two-estimates"
    }}>{`A tale of two estimates`}</h2>
    <p>{`Last Friday I messed up my shoulder, more specifically that tiny little joint between my clavicle and shoulder. I was longboarding, did something wrong and landed on tarmac. Hard.`}</p>
    <p>{`In the morning I had to use my left hand to eat breakfast. Couldn't even pick up a spoon.`}</p>
    <p>{`At the ER the doctors told me that hey, the bones and joint seem fine (no dislocation etc.), but I must have messed up my tendons something bad. What I can expect is three weeks of pain and problems. `}<em parentName="p">{`"Just take some painkillers if it gets too bad"`}</em>{`, they said.`}</p>
    <p>{`Today, 6 days later, my shoulder is already at 90%. Clenching my teeth just a little, I could do 60 of my usual 150 pushups this morning. Even `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Skipping_rope",
        "title": "Skipping rope"
      }}>{`jumping rope`}</a>{` wasn't much of an issue. Yesterday I could even carry a four kilogram longboard in my right arm for extended periods of time.`}</p>
    <p>{`In a day or two I will be fully healed, but essentially `}<strong parentName="p">{`the doctor's estimate was off by almost a factor of 3`}<em parentName="strong">{`.`}</em></strong>{` And I couldn't be happier, it's brightened my whole day up!`}</p>
    <h3 {...{
      "id": "a-programmers-estimate"
    }}>{`A programmer's estimate`}</h3>
    <p>{`I'm working on `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/web-page-segmentation/swizec/4364",
        "title": "Web page segmentation"
      }}>{`a web page segmentation algorithm`}</a>{`. The idea is to take a website and decide which parts are headers, sidebars and so on. It's an interesting problem since everyone else is only concerned with extracting the main juicy content.`}</p>
    <p>{`The algorithm finally started working earlier this week, just a matter of merging the results back into the original `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/HTML",
        "title": "HTML"
      }}>{`HTML`}</a>{` so they can be useful.`}</p>
    <p><em parentName="p">{`I'll have it by Friday morning`}</em>{`, I said.`}</p>
    <p>{`I mean how hard can it be? I'm just using a language I hate (java), am not experienced with, a `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/my-brain-cant-handle-oop-anymore/swizec/4320",
        "title": "My brain can’t handle OOP anymore"
      }}>{`paradigm I dislike`}</a>{` and try to avoid, and a library I only started poking at two weeks ago.`}</p>
    <p>{`Surely I can do a task I don't fully understand by Friday morning! It's the estimate that popped into my mind when I tried to pull something out of my arse, so it must be correct. I've been a programmer for ages!`}</p>
    <p>{`Nope, didn't get it done.`}</p>
    <p>{`Turns out I didn't understand the object hierarchy of `}<a parentName="p" {...{
        "href": "https://code.google.com/p/boilerpipe/"
      }}>{`boilerpipe`}</a>{` - took me many hours just to extend the relevant functions of a particular object.`}</p>
    <p>{`Then I found out the HTML parser I'm using is linear, so there is no clear way of deciding which child nodes belong to what `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Tree_%28data_structure%29",
        "title": "Tree (data structure)"
      }}>{`parent node`}</a>{`.`}</p>
    <p>{`In the end I spent all of my time just figuring out how to add the algorithm's results to the HTML in the crudest way possible - add a number in front of every node in `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Document_Object_Model",
        "title": "Document Object Model"
      }}>{`the DOM`}</a>{`.`}</p>
    <p>{`Well oops.`}</p>
    <h2 {...{
      "id": "programmers-are-optimists"
    }}>{`Programmers are optimists`}</h2>
    <p>{`Whenever you talk to a programmer, just remember, they are an optimistic bunch. Yes, even the most disheartened scruffy old programmer is insanely optimistic. We are the kind of person who:`}</p>
    <ul>
      <li parentName="ul">{`Dives into a project with a spec `}<em parentName="li">{`that will change`}</em></li>
      <li parentName="ul">{`Grabs a bunch of unknown tools, applies them to an unknown domain ... and wins`}</li>
      <li parentName="ul">{`Lies in a different world with every new project`}</li>
      <li parentName="ul">{`Changes all their tools every couple of years`}</li>
    </ul>
    <p>{`Anyone who isn't an optimist just can't survive as a programmer.`}</p>
    <p>{`How many other professions can say that about themselves?`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.javacodegeeks.com/2012/05/top-7-programmers-bad-habits.html"
        }}>{`Top 7 Programmers bad habits`}</a>{` (javacodegeeks.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.skepticblog.org/2012/05/22/the-unknown-unknowns/"
        }}>{`The Unknown Unknowns`}</a>{` (skepticblog.org)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://grokcode.com/722/be-a-paranoid-pessimistic-programmer/"
        }}>{`Be a Paranoid Pessimistic Programmer`}</a>{` (grokcode.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://caroldekkers.wordpress.com/2012/04/10/unknown-unknowns/"
        }}>{`Unknown Unknowns...`}</a>{` (caroldekkers.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://blog.samuellevy.com/index.php?p=post&id=15"
        }}>{`On software communities, "Rock star" programmers, and... Linus`}</a>{` (samuellevy.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=5b66d765-5cc0-41b5-806e-f5439b842b30",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      